import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, AlertTriangle, Info, CheckCircle2, XCircle, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useVertical } from '../context/VerticalContext';
import type { Notification } from '../hooks/useNotifications';
import { notificationRoute } from '../lib/notificationRoutes';
import { reportError } from '../lib/errors';
import {
    fetchActiveBroadcasts,
    broadcastToNotification,
    isBroadcastNotificationId,
    broadcastIdFromNotificationId,
    dismissBroadcast,
} from '../lib/broadcasts';

// Full-page notifications feed. The bell dropdown shows the 20 most recent;
// this is the "View all" destination — same data, more of it, with the app's
// standard --fds-* surface/typography tokens.
const PAGE_SIZE = 100;

const NotificationIcon = ({ type }: { type: Notification['type'] }) => {
    switch (type) {
        case 'warning':
            return <AlertTriangle className="w-4 h-4 text-gold" />;
        case 'success':
            return <CheckCircle2 className="w-4 h-4 text-success" />;
        case 'error':
            return <XCircle className="w-4 h-4 text-danger" />;
        default:
            return <Info className="w-4 h-4 text-info" />;
    }
};

const formatTimeAgo = (dateStr: string): string => {
    const date = new Date(dateStr);
    const diffMs = Date.now() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function NotificationsPage() {
    const { activeOrgId } = useVertical();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(true);

    const unreadCount = notifications.filter((n) => !n.is_read).length;

    const fetchNotifications = useCallback(async () => {
        if (!activeOrgId) return;
        setLoading(true);
        // Scoped by RLS to the caller's org; ordered newest first.
        const { data, error } = await supabase
            .from('notifications')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(PAGE_SIZE);
        // Merge platform broadcasts (global) into the feed.
        const broadcasts = (await fetchActiveBroadcasts()).map(broadcastToNotification);
        const rows = !error && data ? (data as Notification[]) : [];
        setNotifications(
            [...broadcasts, ...rows].sort(
                (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            )
        );
        setLoading(false);
    }, [activeOrgId]);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    const handleClick = async (n: Notification) => {
        if (isBroadcastNotificationId(n.id)) {
            if (!n.is_read) {
                dismissBroadcast(broadcastIdFromNotificationId(n.id));
                setNotifications((prev) =>
                    prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x))
                );
            }
            return;
        }
        if (!n.is_read) {
            // The boolean matters: it is false when the UPDATE matched no row,
            // which is how notifications kept coming back unread before
            // 20260820121000. Dropping it — as this did — turned that into an
            // optimistic flip the next refresh quietly undid.
            const { data: marked, error: markError } = await supabase.rpc('fn_mark_notification_read', {
                p_notification_id: n.id,
            });
            if (markError) reportError(markError, 'notifications-page.mark-notification-read');
            if (marked) {
                setNotifications((prev) =>
                    prev.map((x) => (x.id === n.id ? { ...x, is_read: true, read_at: new Date().toISOString() } : x))
                );
            } else {
                if (!markError) {
                    reportError(
                        new Error(`fn_mark_notification_read matched no row for ${n.id}`),
                        'notifications-page.mark-notification-read',
                    );
                }
                void fetchNotifications();
            }
        }
        const route = notificationRoute(n.metadata);
        if (route) navigate(route);
    };

    const markAllAsRead = async () => {
        notifications.forEach((n) => {
            if (isBroadcastNotificationId(n.id)) dismissBroadcast(broadcastIdFromNotificationId(n.id));
        });
        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true, read_at: new Date().toISOString() })));
        const { error: markAllError } = await supabase.rpc('fn_mark_all_notifications_read');
        if (markAllError) {
            reportError(markAllError, 'notifications-page.mark-all-notifications-read');
            void fetchNotifications();
        }
    };

    const handleDelete = async (n: Notification) => {
        if (isBroadcastNotificationId(n.id)) {
            dismissBroadcast(broadcastIdFromNotificationId(n.id));
        } else {
            const { error: delError } = await supabase.from('notifications').delete().eq('id', n.id);
            if (delError) {
                reportError(delError, 'notifications-page.delete');
                return;
            }
        }
        setNotifications((prev) => prev.filter((x) => x.id !== n.id));
    };

    const clearAll = async () => {
        notifications.forEach((n) => {
            if (isBroadcastNotificationId(n.id)) dismissBroadcast(broadcastIdFromNotificationId(n.id));
        });
        if (activeOrgId) {
            await supabase.from('notifications').delete().eq('org_id', activeOrgId);
        }
        setNotifications([]);
    };

    return (
        <div className="w-full">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-[var(--fds-radius)] bg-raised text-accent">
                        <Bell className="w-5 h-5" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-fg">Notifications</h1>
                        <p className="text-sm text-fg-2">
                            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {unreadCount > 0 && (
                        <button
                            onClick={markAllAsRead}
                            className="text-xs font-medium text-accent hover:underline flex items-center gap-1.5"
                        >
                            <CheckCheck className="w-4 h-4" />
                            Mark all read
                        </button>
                    )}
                    {notifications.length > 0 && (
                        <button
                            onClick={clearAll}
                            className="text-xs font-medium text-fg-2 hover:text-danger hover:underline flex items-center gap-1.5 transition-colors"
                            title="Delete all notifications"
                        >
                            <Trash2 className="w-4 h-4" />
                            Clear all
                        </button>
                    )}
                </div>
            </div>

            {/* List */}
            <div className="bg-panel border border-line rounded-[var(--fds-radius)] overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-fg-2">
                        <Bell className="w-10 h-10 opacity-30 mb-3" />
                        <p className="text-sm">No notifications yet</p>
                        <p className="text-xs opacity-60 mt-1">You're all caught up!</p>
                    </div>
                ) : (
                    <div className="divide-y divide-line">
                        {notifications.map((n) => (
                            <div
                                key={n.id}
                                className={`group relative flex items-stretch hover:bg-raised transition-colors ${
                                    !n.is_read ? 'bg-accent/5' : ''
                                }`}
                            >
                                <button
                                    onClick={() => handleClick(n)}
                                    className="flex-1 min-w-0 text-left px-4 sm:px-5 py-4"
                                >
                                    <div className="flex gap-3">
                                        <div className="flex-shrink-0 mt-0.5">
                                            <NotificationIcon type={n.type} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <p
                                                    className={`text-sm font-medium ${
                                                        n.is_read
                                                            ? 'text-fg-2'
                                                            : 'text-fg'
                                                    }`}
                                                >
                                                    {n.title}
                                                </p>
                                                {!n.is_read && (
                                                    <span className="flex-shrink-0 w-2 h-2 mt-1.5 bg-accent rounded-full" />
                                                )}
                                            </div>
                                            <p className="text-xs text-fg-2 mt-0.5">{n.message}</p>
                                            <p className="text-[10px] text-fg-2 opacity-60 mt-1.5">
                                                {formatTimeAgo(n.created_at)}
                                            </p>
                                        </div>
                                    </div>
                                </button>

                                {/* Delete */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(n);
                                    }}
                                    title="Delete notification"
                                    aria-label="Delete notification"
                                    className="flex-shrink-0 px-4 flex items-center text-fg-2 fd-hover-reveal hover:text-danger"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
